import React, { useState, useEffect, useCallback } from 'react';
import { 
  Shield, 
  AlertTriangle, 
  Clock, 
  CheckCircle,
  XCircle,
  Plus,
  RefreshCw,
  Filter,
  User,
  Calendar,
  FileText,
  Eye,
  Edit,
  ChevronRight,
  Activity,
  AlertCircle
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';
import AsyncSearchSelect from '@/components/common/AsyncSearchSelect';
import { participantLabel, PARTICIPANT_SEARCH_HINT } from '@/components/common/pickerLabels';

const PRACTICE_TYPES = [
  { value: 'SECLUSION', label: 'Seclusion' },
  { value: 'CHEMICAL_RESTRAINT', label: 'Chemical Restraint' },
  { value: 'MECHANICAL_RESTRAINT', label: 'Mechanical Restraint' },
  { value: 'PHYSICAL_RESTRAINT', label: 'Physical Restraint' },
  { value: 'ENVIRONMENTAL_RESTRAINT', label: 'Environmental Restraint' },
];

const STATUS_OPTIONS = [
  { value: 'APPROVED', label: 'Approved', color: 'bg-green-500' },
  { value: 'PENDING', label: 'Pending', color: 'bg-yellow-500' },
  { value: 'EXPIRED', label: 'Expired', color: 'bg-red-500' },
  { value: 'UNDER_REVIEW', label: 'Under Review', color: 'bg-blue-500' },
  { value: 'REVOKED', label: 'Revoked', color: 'bg-gray-500' },
];

const RestrictivePracticesPage = () => {
  const [dashboard, setDashboard] = useState(null);
  const [practices, setPractices] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [reviewsDue, setReviewsDue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showUsageDialog, setShowUsageDialog] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [formData, setFormData] = useState({
    participant_id: '',
    practice_type: 'ENVIRONMENTAL_RESTRAINT',
    description: '',
    justification: '',
    authorisation_number: '',
    authorised_by: '',
    authorisation_date: '',
    expiry_date: '',
    review_date: '',
    review_frequency_days: 90,
    conditions: '',
    safeguards: '',
    notes: ''
  });
  const [usageFormData, setUsageFormData] = useState({
    used_at: new Date().toISOString().slice(0, 16),
    duration_minutes: 30,
    staff_involved: '',
    reason: '',
    outcome: '',
    debrief_conducted: false,
    debrief_notes: ''
  });
  const [reviewFormData, setReviewFormData] = useState({
    review_notes: '',
    next_review_date: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [dashboardRes, practicesRes, expiringRes, reviewsRes] = await Promise.all([
        api.get('/restrictive-practices/dashboard'),
        api.get('/restrictive-practices?include_expired=true'),
        api.get('/restrictive-practices/expiring?days_ahead=60'),
        api.get('/restrictive-practices/reviews-due'),
      ]);
      
      setDashboard(dashboardRes.data?.data);
      setPractices(practicesRes.data?.data || []);
      setExpiring(expiringRes.data?.data || []);
      setReviewsDue(reviewsRes.data?.data || []);
    } catch (err) {
      toast.error(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredPractices = practices.filter(p => {
    if (typeFilter !== 'all' && p.practice_type !== typeFilter) return false;
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    return true;
  });

  const getStatusBadge = (status) => {
    const statusConfig = STATUS_OPTIONS.find(s => s.value === status) || { color: 'bg-gray-500', label: status };
    return <Badge className={`${statusConfig.color} text-white`}>{statusConfig.label}</Badge>;
  };

  const getPracticeTypeLabel = (type) => {
    const typeConfig = PRACTICE_TYPES.find(t => t.value === type);
    return typeConfig?.label || type;
  };

  const getPracticeTypeIcon = (type) => {
    switch (type) {
      case 'SECLUSION': return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'CHEMICAL_RESTRAINT': return <Activity className="w-5 h-5 text-purple-500" />;
      case 'MECHANICAL_RESTRAINT': return <Shield className="w-5 h-5 text-orange-500" />;
      case 'PHYSICAL_RESTRAINT': return <Shield className="w-5 h-5 text-red-500" />;
      case 'ENVIRONMENTAL_RESTRAINT': return <Shield className="w-5 h-5 text-blue-500" />;
      default: return <Shield className="w-5 h-5" />;
    }
  };

  const handleCreatePractice = async () => {
    try {
      const payload = {
        ...formData,
        conditions: formData.conditions ? formData.conditions.split('\n').filter(c => c.trim()) : [],
        safeguards: formData.safeguards ? formData.safeguards.split('\n').filter(s => s.trim()) : [],
        review_frequency_days: parseInt(formData.review_frequency_days)
      };
      
      await api.post('/restrictive-practices', payload);
      toast.success('Restrictive practice authorisation created');
      setShowCreateDialog(false);
      setFormData({
        participant_id: '',
        practice_type: 'ENVIRONMENTAL_RESTRAINT',
        description: '',
        justification: '',
        authorisation_number: '',
        authorised_by: '',
        authorisation_date: '',
        expiry_date: '',
        review_date: '',
        review_frequency_days: 90,
        conditions: '',
        safeguards: '',
        notes: ''
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to create');
    }
  };

  const handleViewDetail = async (practice) => {
    try {
      const res = await api.get(`/restrictive-practices/${practice.id}`);
      setSelectedPractice(res.data?.data);
      setShowDetailDialog(true);
    } catch (err) {
      toast.error('Failed to fetch details');
    }
  };

  const handleLogUsage = async () => {
    try {
      const payload = {
        restrictive_practice_id: selectedPractice.id,
        participant_id: selectedPractice.participant_id,
        used_at: usageFormData.used_at,
        duration_minutes: parseInt(usageFormData.duration_minutes),
        staff_involved: usageFormData.staff_involved.split(',').map(s => s.trim()).filter(s => s),
        reason: usageFormData.reason,
        outcome: usageFormData.outcome,
        debrief_conducted: usageFormData.debrief_conducted,
        debrief_notes: usageFormData.debrief_notes || null
      };
      
      await api.post('/restrictive-practices/usage', payload);
      toast.success('Usage logged successfully');
      setShowUsageDialog(false);
      setUsageFormData({
        used_at: new Date().toISOString().slice(0, 16),
        duration_minutes: 30,
        staff_involved: '',
        reason: '',
        outcome: '',
        debrief_conducted: false,
        debrief_notes: ''
      });
      handleViewDetail(selectedPractice);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to log usage');
    }
  };

  const handleCompleteReview = async () => {
    try {
      const params = new URLSearchParams({
        review_notes: reviewFormData.review_notes,
        next_review_date: reviewFormData.next_review_date
      });
      
      await api.post(`/restrictive-practices/${selectedPractice.id}/review?${params}`);
      toast.success('Review completed successfully');
      setShowReviewDialog(false);
      setReviewFormData({ review_notes: '', next_review_date: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to complete review');
    }
  };

  const SummaryCard = ({ title, value, subtitle, icon: Icon, color, alert }) => (
    <Card className={`border-l-4 ${color}`} data-testid={`summary-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <p className="text-3xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full ${alert ? 'bg-red-100' : 'bg-gray-100'}`}>
            <Icon className={`w-6 h-6 ${alert ? 'text-red-600' : 'text-gray-600'}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="restrictive-practices-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-7 h-7 text-blue-600" />
            Restrictive Practices
          </h1>
          <p className="text-gray-600 mt-1">
            NDIS Quality & Safeguards Commission compliance tracking
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Authorisation
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <SummaryCard
            title="Active Authorisations"
            value={dashboard.summary.active_authorisations}
            icon={CheckCircle}
            color="border-green-500"
          />
          <SummaryCard
            title="Expiring Soon"
            value={dashboard.summary.expiring_soon_30d}
            subtitle="Next 30 days"
            icon={Clock}
            color="border-yellow-500"
            alert={dashboard.summary.expiring_soon_30d > 0}
          />
          <SummaryCard
            title="Expired"
            value={dashboard.summary.expired}
            icon={XCircle}
            color="border-red-500"
            alert={dashboard.summary.expired > 0}
          />
          <SummaryCard
            title="Review Overdue"
            value={dashboard.summary.review_overdue}
            icon={AlertTriangle}
            color="border-orange-500"
            alert={dashboard.summary.review_overdue > 0}
          />
          <SummaryCard
            title="Usage (30d)"
            value={dashboard.summary.usage_last_30d}
            subtitle="Instances logged"
            icon={Activity}
            color="border-blue-500"
          />
        </div>
      )}

      {/* Alerts Section */}
      {(expiring.length > 0 || reviewsDue.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Expiring Soon */}
          {expiring.length > 0 && (
            <Card className="border-yellow-200 bg-yellow-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-yellow-800">
                  <Clock className="w-5 h-5" />
                  Expiring Authorisations
                </CardTitle>
                <CardDescription className="text-yellow-700">
                  {expiring.length} authorisation(s) expiring soon
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {expiring.slice(0, 5).map(item => (
                    <div 
                      key={item.id} 
                      className="flex items-center justify-between p-3 bg-white rounded-lg border border-yellow-200 cursor-pointer hover:bg-yellow-50"
                      onClick={() => handleViewDetail(item)}
                      data-testid={`expiring-item-${item.id}`}
                    >
                      <div className="flex items-center gap-3">
                        {getPracticeTypeIcon(item.practice_type)}
                        <div>
                          <p className="font-medium text-sm">{item.participant_name}</p>
                          <p className="text-xs text-gray-500">{getPracticeTypeLabel(item.practice_type)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className="text-yellow-700 border-yellow-400">
                          {item.days_until_expiry} days
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Reviews Due */}
          {reviewsDue.length > 0 && (
            <Card className="border-orange-200 bg-orange-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-orange-800">
                  <AlertTriangle className="w-5 h-5" />
                  Reviews Overdue
                </CardTitle>
                <CardDescription className="text-orange-700">
                  {reviewsDue.length} review(s) require attention
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {reviewsDue.slice(0, 5).map(item => (
                    <div 
                      key={item.id} 
                      className="flex items-center justify-between p-3 bg-white rounded-lg border border-orange-200 cursor-pointer hover:bg-orange-50"
                      onClick={() => handleViewDetail(item)}
                      data-testid={`review-due-item-${item.id}`}
                    >
                      <div className="flex items-center gap-3">
                        {getPracticeTypeIcon(item.practice_type)}
                        <div>
                          <p className="font-medium text-sm">{item.participant_name}</p>
                          <p className="text-xs text-gray-500">{getPracticeTypeLabel(item.practice_type)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="destructive">
                          {item.days_overdue} days overdue
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* By Type Breakdown */}
      {dashboard && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Authorisations by Type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {PRACTICE_TYPES.map(type => (
                <div 
                  key={type.value}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                  data-testid={`type-count-${type.value}`}
                >
                  {getPracticeTypeIcon(type.value)}
                  <div>
                    <p className="text-2xl font-bold">{dashboard.by_type[type.value] || 0}</p>
                    <p className="text-xs text-gray-500">{type.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters and List */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <CardTitle className="text-lg">All Authorisations</CardTitle>
            <div className="flex gap-2">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[180px]" data-testid="type-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {PRACTICE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]" data-testid="status-filter">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {STATUS_OPTIONS.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredPractices.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No restrictive practice authorisations found</p>
              <p className="text-sm mt-2">Click "New Authorisation" to create one</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredPractices.map(practice => (
                <div 
                  key={practice.id}
                  className="flex items-center justify-between p-4 bg-white border rounded-lg hover:shadow-sm transition-shadow cursor-pointer"
                  onClick={() => handleViewDetail(practice)}
                  data-testid={`practice-item-${practice.id}`}
                >
                  <div className="flex items-center gap-4">
                    {getPracticeTypeIcon(practice.practice_type)}
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{practice.participant_name || 'Unknown Participant'}</p>
                        {getStatusBadge(practice.status)}
                      </div>
                      <p className="text-sm text-gray-600">{getPracticeTypeLabel(practice.practice_type)}</p>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-1">{practice.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right text-sm">
                      {practice.expiry_date && (
                        <p className="text-gray-500">
                          <Calendar className="w-3 h-3 inline mr-1" />
                          Expires: {practice.expiry_date}
                        </p>
                      )}
                      {practice.authorisation_number && (
                        <p className="text-xs text-gray-400">Auth: {practice.authorisation_number}</p>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Restrictive Practice Authorisation</DialogTitle>
            <DialogDescription>
              Create a new NDIS-compliant restrictive practice authorisation
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Participant *</label>
                <AsyncSearchSelect
                  endpoint="/participants"
                  params={{ status: 'ACTIVE' }}
                  value={formData.participant_id}
                  onChange={(v) => setFormData({...formData, participant_id: v})}
                  getOptionLabel={participantLabel}
                  placeholder="Select participant"
                  searchPlaceholder={PARTICIPANT_SEARCH_HINT}
                  emptyText="No participants match that search"
                  testId="create-participant-select"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Practice Type *</label>
                <Select 
                  value={formData.practice_type} 
                  onValueChange={(v) => setFormData({...formData, practice_type: v})}
                >
                  <SelectTrigger data-testid="create-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRACTICE_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium">Description * (min 10 characters)</label>
              <Textarea 
                placeholder="Describe the restrictive practice..."
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                data-testid="create-description"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Justification * (min 10 characters)</label>
              <Textarea 
                placeholder="Clinical justification for using this practice..."
                value={formData.justification}
                onChange={(e) => setFormData({...formData, justification: e.target.value})}
                data-testid="create-justification"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Authorisation Number</label>
                <Input 
                  placeholder="e.g., BSP-2026-001"
                  value={formData.authorisation_number}
                  onChange={(e) => setFormData({...formData, authorisation_number: e.target.value})}
                  data-testid="create-auth-number"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Authorised By</label>
                <Input 
                  placeholder="Name of authorising practitioner"
                  value={formData.authorised_by}
                  onChange={(e) => setFormData({...formData, authorised_by: e.target.value})}
                  data-testid="create-authorised-by"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium">Authorisation Date</label>
                <Input 
                  type="date"
                  value={formData.authorisation_date}
                  onChange={(e) => setFormData({...formData, authorisation_date: e.target.value})}
                  data-testid="create-auth-date"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Expiry Date</label>
                <Input 
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => setFormData({...formData, expiry_date: e.target.value})}
                  data-testid="create-expiry-date"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Review Date</label>
                <Input 
                  type="date"
                  value={formData.review_date}
                  onChange={(e) => setFormData({...formData, review_date: e.target.value})}
                  data-testid="create-review-date"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium">Review Frequency (days)</label>
              <Input 
                type="number"
                min="30"
                max="365"
                value={formData.review_frequency_days}
                onChange={(e) => setFormData({...formData, review_frequency_days: e.target.value})}
                data-testid="create-review-frequency"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Conditions (one per line)</label>
              <Textarea 
                placeholder="Enter conditions, one per line..."
                value={formData.conditions}
                onChange={(e) => setFormData({...formData, conditions: e.target.value})}
                data-testid="create-conditions"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Safeguards (one per line)</label>
              <Textarea 
                placeholder="Enter safeguards, one per line..."
                value={formData.safeguards}
                onChange={(e) => setFormData({...formData, safeguards: e.target.value})}
                data-testid="create-safeguards"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Notes</label>
              <Textarea 
                placeholder="Additional notes..."
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                data-testid="create-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreatePractice}
              disabled={!formData.participant_id || formData.description.length < 10 || formData.justification.length < 10}
              data-testid="create-submit-btn"
            >
              Create Authorisation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedPractice && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <DialogTitle className="flex items-center gap-2">
                      {getPracticeTypeIcon(selectedPractice.practice_type)}
                      {getPracticeTypeLabel(selectedPractice.practice_type)}
                    </DialogTitle>
                    <DialogDescription>
                      {selectedPractice.participant_name}
                    </DialogDescription>
                  </div>
                  {getStatusBadge(selectedPractice.status)}
                </div>
              </DialogHeader>
              <div className="space-y-6 py-4">
                {/* Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Authorisation Number</p>
                    <p className="font-medium">{selectedPractice.authorisation_number || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Authorised By</p>
                    <p className="font-medium">{selectedPractice.authorised_by || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Authorisation Date</p>
                    <p className="font-medium">{selectedPractice.authorisation_date || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Expiry Date</p>
                    <p className="font-medium">{selectedPractice.expiry_date || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Review Date</p>
                    <p className="font-medium">{selectedPractice.review_date || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Review Frequency</p>
                    <p className="font-medium">{selectedPractice.review_frequency_days} days</p>
                  </div>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500 mb-1">Description</p>
                  <p className="bg-gray-50 p-3 rounded">{selectedPractice.description}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500 mb-1">Justification</p>
                  <p className="bg-gray-50 p-3 rounded">{selectedPractice.justification}</p>
                </div>
                
                {selectedPractice.conditions?.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Conditions</p>
                    <ul className="list-disc list-inside bg-gray-50 p-3 rounded">
                      {selectedPractice.conditions.map((c, i) => (
                        <li key={i} className="text-sm">{c}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {selectedPractice.safeguards?.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Safeguards</p>
                    <ul className="list-disc list-inside bg-gray-50 p-3 rounded">
                      {selectedPractice.safeguards.map((s, i) => (
                        <li key={i} className="text-sm">{s}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Usage History */}
                {selectedPractice.usage_history?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">Usage History ({selectedPractice.usage_history.length} records)</p>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {selectedPractice.usage_history.map((usage, i) => (
                        <div key={i} className="bg-gray-50 p-3 rounded text-sm">
                          <div className="flex justify-between">
                            <span className="font-medium">{new Date(usage.used_at).toLocaleString()}</span>
                            <span>{usage.duration_minutes} mins</span>
                          </div>
                          <p className="text-gray-600 mt-1">{usage.reason}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter className="flex gap-2">
                <Button variant="outline" onClick={() => setShowDetailDialog(false)}>Close</Button>
                {selectedPractice.status === 'APPROVED' && (
                  <>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setShowReviewDialog(true);
                        const nextDate = new Date();
                        nextDate.setDate(nextDate.getDate() + (selectedPractice.review_frequency_days || 90));
                        setReviewFormData({
                          review_notes: '',
                          next_review_date: nextDate.toISOString().slice(0, 10)
                        });
                      }}
                      data-testid="complete-review-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Complete Review
                    </Button>
                    <Button 
                      onClick={() => setShowUsageDialog(true)}
                      data-testid="log-usage-btn"
                    >
                      <Activity className="w-4 h-4 mr-2" />
                      Log Usage
                    </Button>
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Log Usage Dialog */}
      <Dialog open={showUsageDialog} onOpenChange={setShowUsageDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Log Restrictive Practice Usage</DialogTitle>
            <DialogDescription>
              Record an instance when this restrictive practice was used
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Date/Time Used *</label>
                <Input 
                  type="datetime-local"
                  value={usageFormData.used_at}
                  onChange={(e) => setUsageFormData({...usageFormData, used_at: e.target.value})}
                  data-testid="usage-datetime"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Duration (minutes) *</label>
                <Input 
                  type="number"
                  min="1"
                  value={usageFormData.duration_minutes}
                  onChange={(e) => setUsageFormData({...usageFormData, duration_minutes: e.target.value})}
                  data-testid="usage-duration"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium">Staff Involved (comma-separated) *</label>
              <Input 
                placeholder="e.g., John Smith, Jane Doe"
                value={usageFormData.staff_involved}
                onChange={(e) => setUsageFormData({...usageFormData, staff_involved: e.target.value})}
                data-testid="usage-staff"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Reason *</label>
              <Textarea 
                placeholder="Why was this practice used?"
                value={usageFormData.reason}
                onChange={(e) => setUsageFormData({...usageFormData, reason: e.target.value})}
                data-testid="usage-reason"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Outcome *</label>
              <Textarea 
                placeholder="What was the outcome?"
                value={usageFormData.outcome}
                onChange={(e) => setUsageFormData({...usageFormData, outcome: e.target.value})}
                data-testid="usage-outcome"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input 
                type="checkbox"
                id="debrief"
                checked={usageFormData.debrief_conducted}
                onChange={(e) => setUsageFormData({...usageFormData, debrief_conducted: e.target.checked})}
                data-testid="usage-debrief-checkbox"
              />
              <label htmlFor="debrief" className="text-sm font-medium">Debrief Conducted</label>
            </div>
            
            {usageFormData.debrief_conducted && (
              <div>
                <label className="text-sm font-medium">Debrief Notes</label>
                <Textarea 
                  placeholder="Notes from the debrief..."
                  value={usageFormData.debrief_notes}
                  onChange={(e) => setUsageFormData({...usageFormData, debrief_notes: e.target.value})}
                  data-testid="usage-debrief-notes"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUsageDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleLogUsage}
              disabled={!usageFormData.staff_involved || !usageFormData.reason || !usageFormData.outcome}
              data-testid="usage-submit-btn"
            >
              Log Usage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Complete Review</DialogTitle>
            <DialogDescription>
              Document the review of this restrictive practice authorisation
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Review Notes * (min 10 characters)</label>
              <Textarea 
                placeholder="Document your review findings..."
                value={reviewFormData.review_notes}
                onChange={(e) => setReviewFormData({...reviewFormData, review_notes: e.target.value})}
                data-testid="review-notes"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Next Review Date *</label>
              <Input 
                type="date"
                value={reviewFormData.next_review_date}
                onChange={(e) => setReviewFormData({...reviewFormData, next_review_date: e.target.value})}
                data-testid="review-next-date"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCompleteReview}
              disabled={reviewFormData.review_notes.length < 10 || !reviewFormData.next_review_date}
              data-testid="review-submit-btn"
            >
              Complete Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RestrictivePracticesPage;
